import Decimal from "decimal.js"
import PropTypes from "prop-types"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"

import Form from "src/components/Form"

import useDebounce from "src/hooks/use_debounce"

// Constants for timing and thresholds
const REPEAT_DELAY_MS = 400
const REPEAT_INTERVAL_MS = 50
const RAPID_ADJUSTMENT_THRESHOLD = 200
const RAPID_ADJUSTMENT_RESET_MS = 2000
const DEBOUNCE_DELAY_MS = 500

// Pure utility functions
const formatDecimalDisplay = (
  decimalValue,
  precision,
  forceMinDecimals = false
) => {
  const inputStr = decimalValue.toString()
  if (inputStr.includes(".")) {
    const [, decimals] = inputStr.split(".")
    if (forceMinDecimals && decimals.length < 2) {
      return decimalValue.toFixed(2)
    } else if (decimals.length <= precision) {
      return inputStr
    } else {
      return decimalValue.toFixed(precision)
    }
  } else {
    return inputStr
  }
}

const calculateStepSize = (displayValue) => {
  if (!displayValue.includes(".")) {
    return new Decimal(1)
  }
  const decimalPlaces = displayValue.split(".")[1].length
  return new Decimal(1).div(new Decimal(10).pow(decimalPlaces))
}

const sanitizeNumericInput = (input) => {
  const sanitized = input.replace(/[^\d.]/g, "")
  const parts = sanitized.split(".")
  return parts[0] + (parts.length > 1 ? "." + parts[1] : "")
}

const NumericInput = ({
  value,
  onChange,
  onBlur,
  disabled,
  hasErrors,
  precision = 2,
  valueMultiplier = 1,
  icon,
  iconPosition = "right",
  inputMode = "decimal",
  maxValue = 999999.99999, // Database limit for precision 11, scale 5
  minValue = 0,
  ...props
}) => {
  const repeatDelayTimeoutRef = useRef(null)
  const repeatIntervalRef = useRef(null)
  const rapidAdjustmentCountRef = useRef(0)
  const lastAdjustmentTimeRef = useRef(Date.now())
  const minDecimal = useMemo(() => new Decimal(minValue), [minValue])
  const maxDecimal = useMemo(() => new Decimal(maxValue), [maxValue])

  const [displayValue, setDisplayValue] = useState(() => {
    const displayDecimal = new Decimal(value).div(valueMultiplier)
    return formatDecimalDisplay(displayDecimal, precision)
  })

  const stepValue = useCallback(
    (direction) => {
      const now = Date.now()

      if (now - lastAdjustmentTimeRef.current > RAPID_ADJUSTMENT_RESET_MS) {
        rapidAdjustmentCountRef.current = 0
      }

      rapidAdjustmentCountRef.current++
      if (rapidAdjustmentCountRef.current > RAPID_ADJUSTMENT_THRESHOLD) {
        console.warn(
          "NumericInput: Rapid adjustment detected, stopping to prevent runaway values"
        )
        clearInterval(repeatIntervalRef.current)
        clearTimeout(repeatDelayTimeoutRef.current)
        repeatIntervalRef.current = null
        repeatDelayTimeoutRef.current = null
        return
      }

      lastAdjustmentTimeRef.current = now

      const currentDecimal = new Decimal(displayValue || "0")

      const step = calculateStepSize(displayValue)

      let newValue =
        direction === "up"
          ? currentDecimal.plus(step)
          : currentDecimal.minus(step)

      newValue = Decimal.max(minDecimal, Decimal.min(maxDecimal, newValue))

      let formattedValue
      if (displayValue.includes(".")) {
        const decimals = displayValue.split(".")[1].length
        formattedValue = newValue.toFixed(decimals)
      } else {
        formattedValue = newValue.toString()
      }
      setDisplayValue(formattedValue)

      const storageValue = newValue.mul(valueMultiplier).toNumber()
      onChange(storageValue)
    },
    [displayValue, onChange, valueMultiplier, minDecimal, maxDecimal]
  )

  const resetRepeatDelay = () => {
    if (repeatDelayTimeoutRef.current) {
      clearTimeout(repeatDelayTimeoutRef.current)
      repeatDelayTimeoutRef.current = null
    }
  }

  const resetRepeatInterval = () => {
    if (repeatIntervalRef.current) {
      clearInterval(repeatIntervalRef.current)
      repeatIntervalRef.current = null
    }
  }

  const resetRapidAdjustmentCount = () => {
    rapidAdjustmentCountRef.current = 0
  }

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key !== "ArrowUp" && e.key !== "ArrowDown") return
      e.preventDefault()

      resetRepeatDelay()
      resetRepeatInterval()
      resetRapidAdjustmentCount()

      const direction = e.key === "ArrowUp" ? "up" : "down"
      stepValue(direction)

      repeatDelayTimeoutRef.current = setTimeout(() => {
        if (!repeatIntervalRef.current) {
          repeatIntervalRef.current = setInterval(() => {
            stepValue(direction)
          }, REPEAT_INTERVAL_MS)
        }
      }, REPEAT_DELAY_MS)
    },
    [stepValue]
  )

  const handleKeyUp = (e) => {
    if (e.key !== "ArrowUp" && e.key !== "ArrowDown") return

    resetRepeatDelay()
    resetRepeatInterval()
    resetRapidAdjustmentCount()
  }

  const updateValueWithClamping = useCallback(
    (newValue, onChange) => {
      if (newValue.trim() === "") {
        return
      }
      const decimalValue = new Decimal(newValue)

      const clampedValue = Decimal.max(
        minDecimal,
        Decimal.min(maxDecimal, decimalValue)
      )

      const storageValue = clampedValue.mul(valueMultiplier).toNumber()
      onChange(storageValue)
    },
    [minDecimal, maxDecimal, valueMultiplier]
  )

  const [debouncedUpdateValue] = useDebounce(
    updateValueWithClamping,
    DEBOUNCE_DELAY_MS
  )

  const handleChange = useCallback(
    (e) => {
      const input = e.target.value
      const cleanValue = sanitizeNumericInput(input)

      if (cleanValue.trim() !== "") {
        try {
          const decimalValue = new Decimal(cleanValue)
          if (decimalValue.gt(maxValue)) {
            return
          }
        } catch (error) {
          console.debug(
            "NumericInput: Invalid decimal input during typing:",
            cleanValue,
            error.message
          )
        }
      }

      setDisplayValue(cleanValue)
      if (cleanValue.trim() !== "") {
        debouncedUpdateValue(cleanValue, onChange)
      }
    },
    [onChange, debouncedUpdateValue, maxValue]
  )

  const handleBlur = useCallback(
    (e) => {
      if (displayValue.trim() === "") {
        setDisplayValue("0")
        onChange(0)
      } else {
        let decimalValue = new Decimal(displayValue)

        decimalValue = Decimal.max(
          minDecimal,
          Decimal.min(maxDecimal, decimalValue)
        )

        const hadDecimals = displayValue.includes(".")
        const formattedValue = formatDecimalDisplay(
          decimalValue,
          precision,
          hadDecimals
        )
        setDisplayValue(formattedValue)

        const storageValue = decimalValue.mul(valueMultiplier).toNumber()
        if (storageValue !== value) {
          onChange(storageValue)
        }
      }
      if (onBlur) {
        onBlur(e)
      }
    },
    [
      displayValue,
      precision,
      onBlur,
      onChange,
      minDecimal,
      maxDecimal,
      valueMultiplier,
      value,
    ]
  )

  useEffect(() => {
    return () => {
      if (repeatDelayTimeoutRef.current) {
        clearTimeout(repeatDelayTimeoutRef.current)
      }
      if (repeatIntervalRef.current) {
        clearInterval(repeatIntervalRef.current)
      }
    }
  }, [])

  return (
    <Form.IconTextField
      type="text"
      inputMode={inputMode}
      value={displayValue}
      onChange={handleChange}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      onKeyUp={handleKeyUp}
      icon={icon}
      position={iconPosition}
      disabled={disabled}
      hasErrors={hasErrors}
      {...props}
    />
  )
}

NumericInput.propTypes = {
  value: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  disabled: PropTypes.bool,
  hasErrors: PropTypes.bool,
  precision: PropTypes.number,
  valueMultiplier: PropTypes.number,
  icon: PropTypes.string,
  iconPosition: PropTypes.oneOf(["left", "right"]),
  inputMode: PropTypes.oneOf(["text", "decimal", "numeric"]),
  id: PropTypes.string,
  maxValue: PropTypes.number,
  minValue: PropTypes.number,
}

export default NumericInput
